import { HOST_URL } from '../../config'
import { HttpStatus } from '../../constants/http-status.enum'
import type { ApiResponse } from '../index'
import type { IClientUID } from '../clients'

export interface IWifiConfig {
    staSsid: string
    staPass: string
}

export enum WifiConfigType {
    staSsid = 0,
    staPass = 1,
}

export interface IWifiConfigUpdate {
    type: WifiConfigType
    value: string
}

export interface IWifiScanResult {
    ssid: string
    rssi: number
    authmode: number
    pairwiseCipher?: number
    groupCipher?: number
    channel: number
}

export enum WifiAuthMode {
    Open = 0 /**< authenticate mode : open */,
    Wep /**< authenticate mode : WEP */,
    WpaPsk /**< authenticate mode : WPA_PSK */,
    Wpa2Psk /**< authenticate mode : WPA2_PSK */,
    WpaWpa2Psk /**< authenticate mode : WPA_WPA2_PSK */,
    Wpa2Enterprise /**< authenticate mode : WPA2_ENTERPRISE */,
    Wpa3Psk /**< authenticate mode : WPA3_PSK */,
    Wpa2Wpa3Psk /**< authenticate mode : WPA2_WPA3_PSK */,
    WapiPsk /**< authenticate mode : WAPI_PSK */,
}

export enum WifiCipherType {
    None = 0 /**< the cipher type is none */,
    Wep40 /**< the cipher type is WEP40 */,
    Wep104 /**< the cipher type is WEP104 */,
    Tkip /**< the cipher type is TKIP */,
    Ccmp /**< the cipher type is CCMP */,
    TkipCcmp /**< the cipher type is TKIP and CCMP */,
    AesCmac128 /**< the cipher type is AES-CMAC-128 */,
    Sms4 /**< the cipher type is SMS4 */,
    Unknown /**< the cipher type is unknown */,
}

export async function getClientWifiConfig(clientId: IClientUID): Promise<ApiResponse<IWifiConfig>> {
    const response = await fetch(`${HOST_URL}wifi-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<IWifiConfig>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function updateClientWifiConfig(clientId: IClientUID, updates: IWifiConfigUpdate[]): Promise<ApiResponse<void>> {
    const response = await fetch(`${HOST_URL}wifi-config`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, updates }),
    })
    const result = (await response.json()) as ApiResponse<void>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function getClientWifiScanResult(clientId: IClientUID): Promise<ApiResponse<IWifiScanResult[]>> {
    const response = await fetch(`${HOST_URL}wifi-config/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<IWifiScanResult[]>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}
